import type { IdToken } from '@auth0/auth0-react';
import { jwtDecode, type JwtPayload } from 'jwt-decode';

import { ReportType } from '@/lib/api/schemas/reports';
import {
  AppMetadata,
  WildcardSublocation,
} from '@/lib/auth/metadata/app-metadata';

export type UserID = Tagged<string, 'UserID'>;

export enum Role {
  SuperAdmin = 'super-admin',
  Validator = 'validator',
  PEO = 'peo',
  ParkDNAStaff = 'parkdna-staff',
}

export enum Subject {
  CUSTOMERS = 'customers',
  BANK_ACCOUNTS = 'bank-accounts',
  LOCATIONS = 'locations',
  LOCATIONS_INTERNAL = 'locations-internal',
  CAMERA_EVENTS = 'camera-events',
  CAMERA_FLEET = 'camera-fleet',
  VIOLATIONS = 'violations',
  VIOLATION_WEBHOOKS = 'violation-webhooks',
  VIOLATION_TRIAGE = 'violation-triage',
  PERMITS = 'permits',
  INVOICES = 'invoices',
  MONTHLY_PARKERS = 'monthly-parkers',
  REPORTS = 'reports',
  RESERVATIONS = 'reservations',
  PARKING_SESSIONS = 'parking-sessions',
  VALIDATIONS = 'validations',
  VALIDATION_CONFIGS = 'validation-configs',
  USERS = 'user-management',
  DASHBOARDS = 'dashboards',
  COMMUNICATIONS = 'communications',
  DEVICES = 'devices',
  DISPUTES = 'disputes',
  REVENUE = 'revenue',
}

export type Action = 'create' | 'read' | 'edit' | 'delete' | 'update' | string;
export type Permission = `${Action}:${Subject}`;

export interface JWTPayload extends JwtPayload {
  permissions: Permission[];
}

const CUSTOM_CLAIMS_NAMESPACE = 'https://parkdna.com';

export class PermissionsService {
  private metadata: AppMetadata;
  private readonly idTokenClaims: IdToken;
  private readonly accessToken: string;
  private readonly userId: UserID;
  private readonly permissions: Permission[];

  private readonly roles: Role[] | null;

  constructor(idTokenClaims: IdToken, accessToken: string) {
    this.idTokenClaims = idTokenClaims;
    this.roles = (idTokenClaims?.[`${CUSTOM_CLAIMS_NAMESPACE}/roles`] ??
      null) as Role[] | null;
    this.accessToken = accessToken;

    const decodedAccessToken = jwtDecode<JWTPayload>(accessToken);
    this.userId = decodedAccessToken.sub as UserID;
    this.permissions = decodedAccessToken.permissions;

    this.metadata = new AppMetadata(idTokenClaims.app_metadata);
  }

  /**
   * Whether the user's location boundary covers every location. Says where the
   * user may act, never what: callers still check the permission.
   * Mirrors the API (ENG-2786): `all_locations` counts only for ParkDNA staff.
   * The API also checks the staff org; the portal cannot, so the API stays the
   * authority and this only shapes the UI.
   */
  hasAllLocations(): boolean {
    return (
      this.isSuperAdmin() ||
      (this.metadata.hasAllLocations() && this.hasRole(Role.ParkDNAStaff))
    );
  }

  hasAccessToLocation(locationId: string) {
    if (!this.hasPermission(Subject.LOCATIONS, 'read')) return false;
    if (this.hasAllLocations()) return true;
    return this.metadata.hasAccessToLocation(locationId);
  }

  hasAccessToSublocation(locationId: string, sublocationId: string) {
    if (!this.hasPermission(Subject.VALIDATION_CONFIGS, 'read')) return false;
    if (this.hasAllLocations()) return true;
    return this.metadata.hasAccessToSublocation(locationId, sublocationId);
  }

  hasAccessToAllSublocationsWithinLocation(locationId: string) {
    return this.hasAccessToSublocation(locationId, WildcardSublocation.ID);
  }

  getSoleValidatorSubLocationId(locationId: string): string | null {
    if (this.hasAllLocations()) {
      return null;
    }
    return this.metadata.getSoleValidatorSubLocationId(locationId);
  }

  hasFullMainLotScope(locationId: string): boolean {
    if (this.hasAllLocations()) {
      return true;
    }
    return this.metadata.hasFullMainLotScope(locationId);
  }

  hasAccessToReport(reportType: ReportType) {
    if (this.holdsLegacySuperAdmin()) return true;
    if (!this.hasPermission(Subject.REPORTS, 'generate')) return false;
    return this.metadata.hasAccessToReport(reportType);
  }

  hasRole(role: string | Role): boolean {
    if (!this.roles) {
      return false;
    }

    return this.roles.map((r) => r.toLowerCase()).includes(role.toLowerCase());
  }

  isSuperAdmin() {
    return this.hasRole(Role.SuperAdmin);
  }

  /**
   * Expand phase of the super-admin retirement: the role still implies every
   * permission so nobody loses access before their Auth0 grants land. Delete
   * this, and every call to it, in the contract phase.
   */
  private holdsLegacySuperAdmin(): boolean {
    return this.isSuperAdmin();
  }

  isGovOfficer() {
    return this.metadata.isGovOfficer();
  }

  hasPermission(subject: Subject, action: Action) {
    if (this.holdsLegacySuperAdmin()) return true;
    return this.permissions?.includes(`${action}:${subject}`) ?? false;
  }

  getAllowedLocationIds(): string[] | 'all' {
    if (this.hasAllLocations()) return 'all';
    return this.metadata.getAllowedLocationIds();
  }

  hasAccessToPortal(): boolean {
    return (
      this.holdsLegacySuperAdmin() ||
      (this.metadata.hasAccessToPortal() && !!this.permissions?.length)
    );
  }

  getOrgName(): string | null {
    return this.idTokenClaims?.['org_display_name'] ?? null;
  }

  getOrgId(): string | null {
    return this.idTokenClaims?.['org_id'] ?? null;
  }

  getOrgMetadata(): Record<string, unknown> | null {
    return (
      this.idTokenClaims?.[`${CUSTOM_CLAIMS_NAMESPACE}/org_metadata`] ?? null
    );
  }

  getUserId(): UserID | null {
    return this.userId;
  }

  getRoles(): Role[] | null {
    return this.roles;
  }

  getPermissions(): Permission[] | null {
    return this.permissions;
  }

  getAccessToken(): string {
    return this.accessToken;
  }
}
